import type { ReactNode } from 'react'
import { cx } from './cx'
import s from './Board.module.css'

export interface BoardItem {
  id: string
  title: string
  group: string | null
  details?: { label: string; value: ReactNode }[]
}

export interface BoardLane {
  key: string
  label: string
}

export interface BoardProps {
  items: BoardItem[]
  /** Host-owned order/labels. Empty declared lanes remain visible. */
  lanes?: BoardLane[]
  onSelectItem?: (item: BoardItem) => void
  ungroupedLabel?: string
  className?: string
}

/** A shared data-free board lens. The host resolves rows and grouping facts. */
export function Board({
  items, lanes: declaredLanes, onSelectItem, ungroupedLabel = 'Ungrouped', className,
}: BoardProps) {
  const lanes = new Map<string, { label: string; items: BoardItem[] }>()
  for (const lane of declaredLanes ?? []) lanes.set(lane.key, { label: lane.label, items: [] })
  for (const item of items) {
    const key = item.group == null || item.group === '' ? '\u0000' : item.group
    const lane = lanes.get(key) ?? { label: key === '\u0000' ? ungroupedLabel : key, items: [] }
    lane.items.push(item)
    lanes.set(key, lane)
  }

  return (
    <div className={cx(s.board, className)} data-testid="board" tabIndex={0}>
      {[...lanes.entries()].map(([key, lane]) => (
        <section className={s.lane} key={key} aria-labelledby={`board-lane-${encodeURIComponent(key)}`}>
          <h3 className={s.laneHead} id={`board-lane-${encodeURIComponent(key)}`}>
            <span>{lane.label}</span><span className={s.count}>{lane.items.length}</span>
          </h3>
          <div className={s.cards}>
            {lane.items.map((item) => {
              const content = <>
                <span className={s.title}>{item.title}</span>
                {(item.details ?? []).map((detail) => (
                  <span className={s.detail} key={detail.label}>
                    <span className={s.detailLabel}>{detail.label}</span><span>{detail.value}</span>
                  </span>
                ))}
              </>
              return onSelectItem
                ? <button key={item.id} type="button" className={s.card} onClick={() => onSelectItem(item)}>{content}</button>
                : <article key={item.id} className={s.card}>{content}</article>
            })}
          </div>
        </section>
      ))}
      {items.length === 0 && !declaredLanes?.length && <div className={s.empty}>No items in this view.</div>}
    </div>
  )
}
